/**
 * Materials API client (Phase 3 Step 4).
 * Uses relative `/api` — Vite proxy injects X-Api-Token server-side.
 * Do NOT set X-Api-Token from the browser.
 */

import type { Material, MaterialInput, MaterialKind, MaterialStatus } from './types'

export type MaterialsApiError = {
  ok: false
  error: { code: string; message: string; status: number }
}

export class MaterialsApiClientError extends Error {
  readonly code: string
  readonly status: number

  constructor(code: string, message: string, status: number) {
    super(message)
    this.name = 'MaterialsApiClientError'
    this.code = code
    this.status = status
  }
}

type MaterialApiDto = {
  id: string
  code: string
  name: string
  kind: MaterialKind
  status: MaterialStatus
  uom_default: string | null
  notes: string | null
  group_code?: string | null
  attributes?: Record<string, any>
  created_by: string | null
  updated_by: string | null
  version: number
  created_at: string
  updated_at: string
}

async function parseJson<T>(res: Response): Promise<T> {
  return (await res.json()) as T
}

function throwIfApiError(res: Response, body: unknown, fallback: string): void {
  if (res.ok) return
  const err = body as MaterialsApiError
  throw new MaterialsApiClientError(
    err.error?.code ?? 'HTTP_ERROR',
    err.error?.message ?? `${fallback} (${res.status})`,
    err.error?.status ?? res.status,
  )
}

/** Map API DTO → UI Material. */
export function materialApiDtoToUi(dto: MaterialApiDto): Material {
  return {
    id: dto.id,
    code: dto.code,
    name: dto.name,
    kind: dto.kind,
    status: dto.status,
    uom_default: dto.uom_default ?? undefined,
    notes: dto.notes ?? undefined,
    group_code: dto.group_code ?? undefined,
    attributes: dto.attributes ?? undefined,
    created_by: dto.created_by ?? undefined,
    updated_by: dto.updated_by ?? undefined,
    version: dto.version,
    created_at: dto.created_at,
    updated_at: dto.updated_at,
  }
}

export async function listMaterialsApi(): Promise<Material[]> {
  const res = await fetch('/api/materials')
  const body = await parseJson<
    { ok: true; materials: MaterialApiDto[] } | MaterialsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to list materials')
  return (body as { materials: MaterialApiDto[] }).materials.map(
    materialApiDtoToUi,
  )
}

export async function getMaterialApi(id: string): Promise<Material | null> {
  const res = await fetch(`/api/materials/${encodeURIComponent(id)}`)
  if (res.status === 404) return null
  const body = await parseJson<
    { ok: true; material: MaterialApiDto } | MaterialsApiError
  >(res)
  throwIfApiError(res, body, 'Failed to get material')
  return materialApiDtoToUi((body as { material: MaterialApiDto }).material)
}

function materialInputToBody(input: MaterialInput): Record<string, unknown> {
  return {
    code: input.code,
    name: input.name,
    kind: input.kind,
    status: input.status,
    uom_default: input.uom_default,
    notes: input.notes,
    group_code: input.group_code,
    attributes: input.attributes,
  }
}

export async function createMaterialApi(input: MaterialInput): Promise<Material> {
  const res = await fetch('/api/materials', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(materialInputToBody(input)),
  })
  const body = await parseJson<
    { ok: true; material: MaterialApiDto } | MaterialsApiError
  >(res)
  throwIfApiError(res, body, 'Tạo Material thất bại')
  return materialApiDtoToUi((body as { material: MaterialApiDto }).material)
}

export async function patchMaterialApi(
  id: string,
  patch: Partial<MaterialInput> & {
    group_code?: string | null
    attributes?: Record<string, any>
  },
): Promise<Material> {
  const res = await fetch(`/api/materials/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(patch),
  })
  const body = await parseJson<
    { ok: true; material: MaterialApiDto } | MaterialsApiError
  >(res)
  throwIfApiError(res, body, 'Cập nhật vật tư thất bại')
  return materialApiDtoToUi((body as { material: MaterialApiDto }).material)
}

export async function deleteMaterialApi(id: string): Promise<{ ok: boolean }> {
  const res = await fetch(`/api/materials/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  })
  const body = await parseJson<{ ok: true } | MaterialsApiError>(res)
  throwIfApiError(res, body, 'Xóa vật tư thất bại')
  return body as { ok: true }
}


/** Lưu trữ vật tư (ẩn khỏi Tồn kho; tự xóa sau 30 ngày nếu không phát sinh gì). */
export async function archiveMaterialApi(id: string): Promise<void> {
  const res = await fetch(`/api/materials/${encodeURIComponent(id)}/archive`, { method: 'POST', headers: { 'X-Requested-With': 'erp' } })
  const body = await parseJson<{ ok: true } | MaterialsApiError>(res)
  throwIfApiError(res, body, 'Lưu trữ vật tư thất bại')
}

/** Khôi phục vật tư từ lưu trữ. */
export async function restoreMaterialApi(id: string): Promise<void> {
  const res = await fetch(`/api/materials/${encodeURIComponent(id)}/restore`, { method: 'POST', headers: { 'X-Requested-With': 'erp' } })
  const body = await parseJson<{ ok: true } | MaterialsApiError>(res)
  throwIfApiError(res, body, 'Khôi phục vật tư thất bại')
}
